import UntitledIcon from '@payaca/untitled-icons';
import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import Button from '../../Button/Button';
import Checkbox from '../../Checkbox/Checkbox';
import Radio from '../../Radio/Radio';
import Sidebar, { SidebarProps } from '../../Sidebar/Sidebar';

export type FilterOption =
  | {
      title: string;
      name: string;
      type: 'checkbox';
      options: { label: string; value: string | number }[];
    }
  | {
      title: string;
      name: string;
      type: 'radio';
      options: { label: string; value: string | number | boolean }[];
    };

type CheckboxAppliedFilter = (string | number)[] | null;
type RadioAppliedFilter = string | number | boolean | null;
export type AppliedFilters =
  | { [key: string]: CheckboxAppliedFilter | RadioAppliedFilter }
  | undefined;

export interface IProps extends Pick<SidebarProps, 'isOpen' | 'onClose'> {
  options: FilterOption[];
  appliedFilters?: AppliedFilters;
  onApplyFilters: (filters: AppliedFilters) => void;
}
const MultiFilterSidebar: FC<IProps> = (props) => {
  const { isOpen, onClose, options, appliedFilters, onApplyFilters } = props;
  const [_appliedFilters, _setAppliedFilters] = useState(appliedFilters);

  useEffect(() => {
    _setAppliedFilters(appliedFilters);
  }, [appliedFilters]);

  const onChangeFilterOption = useCallback(
    (filterName: string, filterValue: any) => {
      _setAppliedFilters({
        ..._appliedFilters,
        [filterName]: filterValue,
      });
    },
    [_appliedFilters]
  );

  const renderFilterOption = (filter: FilterOption, i: number) => {
    if (filter.options) {
      switch (filter.type) {
        case 'checkbox': {
          const currentFilter =
            (_appliedFilters?.[filter.name] as CheckboxAppliedFilter) || [];
          return (
            <FilterSection
              title={filter.title}
              key={`filter-${i}`}
              onClearFilter={() => onChangeFilterOption(filter.name, [])}
            >
              {filter.options.map((opt) => {
                return (
                  <div key={`filter-option-${opt.value}`}>
                    <Checkbox
                      label={opt.label}
                      checked={currentFilter.includes(opt.value)}
                      onChange={() => {
                        if (currentFilter.includes(opt.value)) {
                          // remove from filter
                          onChangeFilterOption(
                            filter.name,
                            currentFilter.filter((o: any) => o !== opt.value)
                          );
                        } else {
                          // add to filter
                          onChangeFilterOption(filter.name, [
                            ...currentFilter,
                            opt.value,
                          ]);
                        }
                      }}
                    />
                  </div>
                );
              })}
            </FilterSection>
          );
        }
        case 'radio': {
          const currentFilter = _appliedFilters?.[
            filter.name
          ] as RadioAppliedFilter;

          return (
            <FilterSection
              title={filter.title}
              key={`filter-${i}`}
              onClearFilter={() => onChangeFilterOption(filter.name, null)}
            >
              {filter.options.map((opt) => {
                return (
                  <div key={`filter-option-${opt.value}`}>
                    <Radio
                      label={opt.label}
                      checked={currentFilter === opt.value}
                      onChange={() => {
                        onChangeFilterOption(filter.name, opt.value);
                      }}
                    />
                  </div>
                );
              })}
            </FilterSection>
          );
        }
      }
    }
  };

  const clearAllFilters = useCallback(() => {
    const allEmptyFilters = options.reduce((acc: Record<string, any>, curr) => {
      acc[curr.name] = curr.type === 'radio' ? null : [];
      return acc;
    }, {});
    onApplyFilters?.(allEmptyFilters);
  }, [options, onApplyFilters]);

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} title="Filter">
      <Sidebar.Body>
        <div className="p-5">{options.map(renderFilterOption)}</div>
      </Sidebar.Body>
      <Sidebar.Footer>
        <div className="flex items-center gap-x-2">
          <Button
            variant="secondary"
            size={'sm'}
            className="w-full"
            onClick={() => {
              clearAllFilters();
              onClose?.();
            }}
          >
            <UntitledIcon name="x-close" className="h-2.5 w-2.5" />
            Clear all filters
          </Button>
          <Button
            size={'sm'}
            className="w-full"
            onClick={() => {
              onApplyFilters(_appliedFilters);
              onClose?.();
            }}
          >
            Save
          </Button>
        </div>
      </Sidebar.Footer>
    </Sidebar>
  );
};

type FilterSectionProps = {
  title: string;
  onClearFilter?: () => void;
};
const FilterSection: FC<PropsWithChildren<FilterSectionProps>> = ({
  title,
  children,
  onClearFilter,
}) => (
  <div className="mb-5 border-b border-gray-200 pb-5 last:mb-0 last:border-b-0 last:pb-0">
    <label className="text-xs font-semibold uppercase text-gray-800">
      {title}
    </label>
    <div className="mt-3 grid grid-cols-2">{children}</div>
    <Button
      variant="tertiary"
      size={'xs'}
      className="mt-3 inline-flex items-center px-2.5 py-1.5 text-xs font-medium"
      onClick={onClearFilter}
    >
      <UntitledIcon name="x-close" className="h-2 w-2" />
      Clear
    </Button>
  </div>
);
export default MultiFilterSidebar;
